import { signOut } from "firebase/auth";
import {
  doc,
  getDocFromServer,
  terminate,
  clearIndexedDbPersistence,
  waitForPendingWrites
} from "firebase/firestore";
import { db } from "./firebaseConfig";
import { clearPack } from "../../helpers/offlinePack";
import { USERS } from "../../../constants";

// Give up on flushing rather than hanging on an unreachable server.
const FLUSH_TIMEOUT_MS = 5000;

export default async function firebaseSignOutWrapper(auth) {
  console.log("In firebaseSignOutWrapper...");

  // Read this before signOut, which clears currentUser.
  const email = auth.currentUser?.email;

  // Firestore now keeps the signed-in user's whole document — progress, the
  // name on their certificate, the certificate URL — in IndexedDB, so signing
  // out should clear it rather than leave it readable at rest to the next
  // person on a shared browser.
  //
  // But clearIndexedDbPersistence destroys the pending mutation queue along
  // with the cache, and terminate() does not flush it. Clearing with unsynced
  // answers still queued would silently discard exactly the offline progress
  // the app promises to sync. So the cache is only cleared once those writes
  // are confirmed at the server; otherwise it is left alone and flushes on the
  // next sign-in from this browser.
  //
  // Two things make the flush easy to get wrong:
  //
  // It MUST happen before signOut. Firestore keys its mutation queue per user,
  // so afterwards the queue is the anonymous user's empty one, and
  // waitForPendingWrites would resolve at once on nothing — clearing away the
  // very writes it is meant to protect.
  //
  // And on its own it proves nothing: with an empty queue it resolves at once
  // whether or not there is a network, so it would report success on a plane
  // and clear a cache the user still needs. navigator.onLine is no help either
  // — it is true on a captive portal, which is the whole reason the quiz round
  // has a fallback for that case. So connectivity is established the only way
  // that actually proves it, with a forced round-trip to the server.
  let flushed = false;
  if (email) {
    try {
      await Promise.race([
        (async () => {
          await getDocFromServer(doc(db, USERS, email));
          await waitForPendingWrites(db);
        })(),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("flush timed out")),
            FLUSH_TIMEOUT_MS
          )
        )
      ]);
      flushed = true;
    } catch (error) {
      console.log("Could not confirm a flush; keeping the local cache:", error);
    }
  }

  try {
    await signOut(auth);
    console.log("Sign out successful");
  } catch (error) {
    // Reload rather than return: play.js has already disabled the button and
    // changed its text, so returning here leaves it reading "Signing out..."
    // with pointer-events: none and no way back.
    console.log(error);
    window.location.reload();
    return;
  }

  if (flushed) {
    try {
      await terminate(db);
      // Rejects while another tab holds the persistence lease. Keeping the
      // cache is the correct outcome there too.
      await clearIndexedDbPersistence(db);
      // Only once the cache is actually gone. The pack is just a list of ids
      // into that cache, so the two are cleared together or kept together —
      // clearing it alone would tell the next offline session it has no
      // questions while 30 playable ones are still sitting there.
      clearPack(email);
    } catch (error) {
      console.log("Could not clear the local cache:", error);
    }
  }

  // A terminated client cannot be reused, and the page must not keep rendering
  // against one, so reload either way.
  window.location.reload();
}
